import React, { useMemo, useState, useEffect } from "react";
import {
  Alert,
  Box,
  Typography,
  useTheme,
  Button,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
  Switch,
  FormControlLabel,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import Sidebar from "../../components/sidebar/Sidebar";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import { useGetCompaniesQuery } from "../../store/apis/companyApi";
import { useCreateBriefingMutation } from "../../store/apis/briefingApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { uploadBriefingImage } from "../../firebase/firebaseStorage";
import { components } from "../../types/api";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";

type BriefingType = components["schemas"]["_36_Enums.BriefingType"];

interface FormState {
  name: string;
  companyId: string;
  briefingType: BriefingType | "";
  campaignName: string;
  brandOrProduct: string;
  month: number;
  year: number;
  totalBudget: string;
  fee: string;
  trackingPixel: boolean;
  hasTags: boolean;
  notes: string;
  imageUrl?: string;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

const currentDate = dayjs();

const defaultState: FormState = {
  name: "",
  companyId: "",
  briefingType: "",
  campaignName: "",
  brandOrProduct: "",
  month: currentDate.month() + 1,
  year: currentDate.year(),
  totalBudget: "",
  fee: "",
  trackingPixel: false,
  hasTags: false,
  notes: "",
  imageUrl: "",
};

const NewBriefing: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const collapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);

  const canManageBriefings =
    userDb?.role === "ADMIN" || userDb?.role === "ACCOUNT";

  const {
    data: companiesResponse,
    isLoading: isLoadingCompanies,
    error: companiesError,
  } = useGetCompaniesQuery(undefined, { skip: !canManageBriefings });

  const [createBriefing, { isLoading: isCreating }] =
    useCreateBriefingMutation();

  const [formState, setFormState] = useState<FormState>(defaultState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const companyOptions = useMemo(
    () =>
      companiesResponse?.data?.map((company) => ({
        id: company.id,
        name: company.nameCommercial || company.nameFiscal || company.id,
        feeVariable: company.feeVariable || 0,
      })) ?? [],
    [companiesResponse]
  );

  const briefingTypeOptions = useMemo<BriefingType[]>(
    () => ["PAID_MEDIA_MANAGED", "BUY_MEDIA_DIRECT_AND_OWN"],
    []
  );

  // Automated Briefing Name Generation
  useEffect(() => {
    const { month, year, campaignName, brandOrProduct, briefingType } =
      formState;
    if (month && year && campaignName && briefingType) {
      const paddedMonth = month.toString().padStart(2, "0");
      // Formula: Month_Year_BriefingType_Brand_Campaign
      const parts = [
        paddedMonth,
        year,
        briefingType,
        brandOrProduct,
        campaignName,
      ];

      const newName = parts.filter((part) => part).join("_");

      setFormState((prev) => {
        if (prev.name !== newName) {
          return { ...prev, name: newName };
        }
        return prev;
      });
    }
  }, [
    formState.month,
    formState.year,
    formState.campaignName,
    formState.brandOrProduct,
    formState.briefingType,
    formState,
  ]);

  if (!canManageBriefings) {
    return <Navigate to="/" replace />;
  }

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingImage(true);
      // We use a temporary ID or "new" since the briefing doesn't exist yet.
      // Ideally we would upload after creation, but to keep UX simple we upload first.
      // Using "temp" folder in storage rules might be better, or just allow "new" path.
      // For now we will use a timestamp based path in the storage function logic if needed,
      // but here we just pass a placeholder ID "new-briefing" which we might need to handle in rules/cleanup.
      // Actually, let's use a random ID for the folder to avoid collisions if multiple users create at once.
      // But we don't have the ID yet. A common pattern is to upload to a temp location.
      // Given the rules allow write if auth, we can stick to a convention.
      const tempId = `temp-${Date.now()}`;
      const url = await uploadBriefingImage(file, tempId);

      setFormState((prev) => ({ ...prev, imageUrl: url }));
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.imageUploadSuccess",
            "Image uploaded successfully"
          ),
          severity: "success",
        })
      );
    } catch (error) {
      console.error("Error uploading image:", error);
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.targetImageUploadError",
            "Failed to upload image"
          ),
          severity: "error",
        })
      );
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setFormState((prev) => ({ ...prev, imageUrl: "" }));
  };

  const handleChange = (
    field: keyof FormState,
    value: string | number | boolean
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const nextErrors: FormErrors = {};
    if (!formState.name.trim()) {
      nextErrors.name = t("briefingsCreate.errors.required");
    }
    if (!formState.companyId) {
      nextErrors.companyId = t("briefingsCreate.errors.required");
    }
    if (!formState.briefingType) {
      nextErrors.briefingType = t("briefingsCreate.errors.required");
    }
    if (!formState.month || formState.month < 1 || formState.month > 12) {
      nextErrors.month = t("briefingsCreate.errors.invalidMonth");
    }
    if (!formState.year || formState.year < 2000) {
      nextErrors.year = t("briefingsCreate.errors.invalidYear");
    }
    if (!formState.totalBudget.trim()) {
      nextErrors.totalBudget = t("briefingsCreate.errors.required");
    } else if (Number.isNaN(Number(formState.totalBudget))) {
      nextErrors.totalBudget = t("briefingsCreate.errors.invalidNumber");
    }
    if (!formState.fee.trim()) {
      nextErrors.fee = t("briefingsCreate.errors.required");
    } else if (Number.isNaN(Number(formState.fee))) {
      nextErrors.fee = t("briefingsCreate.errors.invalidNumber");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) {
      return;
    }

    try {
      await createBriefing({
        name: formState.name.trim(),
        companyId: formState.companyId,
        briefingType: formState.briefingType as BriefingType,
        campaignName: formState.campaignName.trim() || undefined,
        brandOrProduct: formState.brandOrProduct.trim() || undefined,
        month: Number(formState.month),
        year: Number(formState.year),
        totalBudget: Number(formState.totalBudget),
        fee: Number(formState.fee),
        trackingPixel: formState.trackingPixel,
        hasTags: formState.hasTags,
        notes: formState.notes.trim() || undefined,
        imageUrl: formState.imageUrl || undefined,
      }).unwrap();

      dispatch(
        setToastMessage({
          message: t("briefingsCreate.toast.success"),
          severity: "success",
        })
      );
      navigate("/briefings");
    } catch (error: unknown) {
      console.error("Create briefing failed", error);
      dispatch(
        setToastMessage({
          message: t("briefingsCreate.toast.error"),
          severity: "error",
        })
      );
    }
  };

  const handleCancel = () => {
    navigate("/briefings");
  };

  const calculatedFieldStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.info.light + "15",
      "& fieldset": {
        borderColor: theme.palette.info.main,
        borderWidth: "2px",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.info.dark,
      },
      "&.Mui-disabled": {
        backgroundColor: theme.palette.info.light + "20",
        "& fieldset": {
          borderColor: theme.palette.info.main,
        },
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.info.dark,
      fontWeight: 600,
      "&.Mui-disabled": {
        color: theme.palette.info.dark,
      },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: theme.palette.text.primary,
      color: theme.palette.text.primary,
    },
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />

      <Box
        sx={{
          flexGrow: 1,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} + 12px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <Box display="flex" flexDirection="column" gap={1}>
            <Button
              variant="text"
              startIcon={<KeyboardBackspaceIcon />}
              sx={{ alignSelf: "flex-start" }}
              onClick={handleCancel}
            >
              {t("briefingsCreate.back")}
            </Button>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 700,
                color: theme.palette.grey[900],
              }}
            >
              {t("briefingsCreate.title")}
            </Typography>
            <Typography variant="body2" color={theme.palette.grey[600]}>
              {t("briefingsCreate.subtitle")}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 4 },
            position: "relative",
          }}
        >
          {isLoadingCompanies && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255,255,255,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 4,
                zIndex: 1,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}

          {companiesError && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="error">
                {t("briefingsCreate.errors.companyLoad")}
              </Alert>
            </Box>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 4 }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("briefingsCreate.form.campaignName")}
                  value={formState.campaignName}
                  onChange={(event) =>
                    handleChange("campaignName", event.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={Boolean(errors.companyId)}>
                  <InputLabel id="briefing-company">
                    {t("briefingsCreate.form.company")}
                  </InputLabel>
                  <Select
                    labelId="briefing-company"
                    label={t("briefingsCreate.form.company")}
                    value={formState.companyId}
                    onChange={(event) => {
                      const selectedId = event.target.value as string;
                      const selectedComp = companyOptions.find(
                        (c) => c.id === selectedId
                      );

                      setFormState((prev) => ({
                        ...prev,
                        companyId: selectedId,
                        ...(selectedComp && {
                          fee: String(selectedComp.feeVariable),
                        }),
                      }));

                      if (errors.companyId) {
                        setErrors((prev) => ({
                          ...prev,
                          companyId: undefined,
                        }));
                      }
                    }}
                  >
                    {companyOptions.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.companyId && (
                    <FormHelperText>{errors.companyId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={Boolean(errors.briefingType)}>
                  <InputLabel id="briefing-type">
                    {t("briefingsCreate.form.briefingType")}
                  </InputLabel>
                  <Select
                    labelId="briefing-type"
                    label={t("briefingsCreate.form.briefingType")}
                    value={formState.briefingType}
                    onChange={(event) =>
                      handleChange(
                        "briefingType",
                        event.target.value as BriefingType
                      )
                    }
                  >
                    {briefingTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {t(`briefingsCreate.form.briefingTypeOptions.${type}`)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.briefingType && (
                    <FormHelperText>{errors.briefingType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t("briefingsCreate.form.month")}
                  value={formState.month}
                  onChange={(event) =>
                    handleChange("month", Number(event.target.value))
                  }
                  inputProps={{ min: 1, max: 12 }}
                  error={Boolean(errors.month)}
                  helperText={errors.month}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t("briefingsCreate.form.year")}
                  value={formState.year}
                  onChange={(event) =>
                    handleChange("year", Number(event.target.value))
                  }
                  inputProps={{ min: 2000 }}
                  error={Boolean(errors.year)}
                  helperText={errors.year}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t("briefingsCreate.form.totalBudget", "Total budget")}
                  value={formState.totalBudget}
                  onChange={(event) =>
                    handleChange("totalBudget", event.target.value)
                  }
                  error={Boolean(errors.totalBudget)}
                  helperText={errors.totalBudget}
                  inputProps={{ step: "any" }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t("briefingsCreate.form.fee", "Fee")}
                  value={formState.fee}
                  onChange={(event) => handleChange("fee", event.target.value)}
                  error={Boolean(errors.fee)}
                  helperText={errors.fee}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  inputProps={{ step: "any" }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("briefingsCreate.form.brandOrProduct")}
                  value={formState.brandOrProduct}
                  onChange={(event) =>
                    handleChange("brandOrProduct", event.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("briefingsCreate.form.name")}
                  value={formState.name}
                  onChange={(event) => handleChange("name", event.target.value)}
                  error={Boolean(errors.name)}
                  helperText={errors.name}
                  disabled
                  sx={calculatedFieldStyles}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t("briefingsCreate.form.notes")}
                  value={formState.notes}
                  onChange={(event) =>
                    handleChange("notes", event.target.value)
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                    },
                    "& .MuiOutlinedInput-inputMultiline": {
                      padding: theme.spacing(2),
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" spacing={4} flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formState.trackingPixel}
                        onChange={(event) =>
                          handleChange("trackingPixel", event.target.checked)
                        }
                      />
                    }
                    label={t("briefingsCreate.form.trackingPixel")}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formState.hasTags}
                        onChange={(event) =>
                          handleChange("hasTags", event.target.checked)
                        }
                      />
                    }
                    label={t("briefingsCreate.form.hasTags")}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {t("briefingsCreate.form.image", "Briefing Image")}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  {formState.imageUrl ? (
                    <Box sx={{ position: "relative", display: "inline-block" }}>
                      <img
                        src={formState.imageUrl}
                        alt="Briefing attachment"
                        style={{
                          height: 100,
                          width: "auto",
                          borderRadius: 4,
                          objectFit: "cover",
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={handleRemoveImage}
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          bgcolor: "background.paper",
                          boxShadow: 1,
                          "&:hover": { bgcolor: "background.paper" },
                        }}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={
                        isUploadingImage ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CloudUploadIcon />
                        )
                      }
                      disabled={isUploadingImage}
                    >
                      {isUploadingImage
                        ? t("common.uploading", "Uploading...")
                        : t("common.uploadImage", "Upload Image")}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="flex-end"
              sx={{ mt: 4 }}
            >
              <Button variant="outlined" onClick={handleCancel} fullWidth>
                {t("briefingsCreate.actions.cancel")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isCreating}
                fullWidth
              >
                {isCreating
                  ? t("briefingsCreate.actions.submitting")
                  : t("briefingsCreate.actions.submit")}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default NewBriefing;
